import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { motion, Variants } from "motion/react";
import { capturePosthog } from "@/lib/posthog";
import { InteractiveDemo } from "./demo/InteractiveDemo";
import { SuccessIcon, ShieldIcon } from "@/components/icons/status";

// Entrée du héros : courte (0,5 s) et resserrée (50 ms par rang) pour que tout soit en place en moins d'une seconde.
const fadeUpVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: delay * 0.05,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
};

const fadeUpInstantVariants: Variants = {
  visible: { opacity: 1, y: 0, transition: { duration: 0 } },
};

const popInVariants: Variants = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: (delay: number) => ({
    opacity: 1,
    scale: 1,
    transition: {
      delay: delay * 0.05,
      type: "spring",
      stiffness: 200,
      damping: 20,
    },
  }),
};

const Hero = () => {
  const trackLandingCta = (ctaId: string, ctaLabel: string) => {
    const isDemo = ctaId === "hero_view_how_it_works";
    capturePosthog(isDemo ? "landing_cta_demo_clicked" : "landing_cta_try_free_clicked", {
      cta_id: ctaId,
      cta_label: ctaLabel,
      cta_section: "hero",
    });
    capturePosthog("landing_cta_clicked", {
      cta_id: ctaId,
      cta_label: ctaLabel,
      cta_section: "hero",
      cta_destination: isDemo ? "/#how-it-works" : "/auth?mode=signup",
    });
  };

  return (
    <section className="relative pt-28 pb-20 md:pt-36 md:pb-32 overflow-hidden bg-mesh-aurora">
      {/* Subtle dot grid texture */}
      <div className="absolute inset-0 -z-10 pointer-events-none bg-dot-grid opacity-40" />

      {/* Animated colored halos */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full -z-10 pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.22), transparent 70%)", filter: "blur(60px)" }}
      />
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        className="absolute top-20 -right-32 w-[460px] h-[460px] rounded-full -z-10 pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--accent) / 0.22), transparent 70%)", filter: "blur(60px)" }}
      />

      {/* Curved connection lines in the background */}
      <svg className="absolute top-0 left-0 w-full h-[800px] -z-10 pointer-events-none opacity-20" viewBox="0 0 1000 800" preserveAspectRatio="none">
         <path d="M-100,300 C200,300 300,100 500,100 C700,100 800,400 1100,400" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="6 6" />
         <path d="M-100,500 C200,500 400,600 600,400 C800,200 900,300 1100,300" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="6 6" />
      </svg>

      <div className="relative container mx-auto px-4">
        <div className="relative max-w-[1000px] mx-auto text-center">
          {/* Trust badge — neutral, no fake testimonial */}
          <motion.div
            custom={2}
            initial="hidden"
            animate="visible"
            variants={fadeUpVariants}
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full glass-pill text-foreground text-payfit-body mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-success shadow-[0_0_8px_rgba(30,173,123,0.6)] animate-pulse" />
            Conçu pour les supérettes et franchises alimentaires — IDCC 1505
          </motion.div>

          {/* Title with display-italic accent */}
          <div className="overflow-hidden">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-[88px] font-black tracking-[-0.04em] text-foreground leading-[1.02] mb-6">
              Le planning de votre <br className="hidden md:block" />
              <span className="text-display-headline gradient-text font-normal">supérette, prêt en 5 min.</span>
            </h1>
          </div>

          {/* Subtitle */}
          <motion.p
            initial={false}
            animate="visible"
            variants={fadeUpInstantVariants}
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-4 leading-relaxed font-medium"
          >
            Dupliquez votre semaine type, ajustez les horaires, publiez. Vos salariés reçoivent leur planning sur leur téléphone et l'export paie est prêt à la fin du mois.
          </motion.p>

          {/* ICP signal — entity anchor for SEO/GEO */}
          <motion.p
            initial={false}
            animate="visible"
            variants={fadeUpInstantVariants}
            className="text-sm md:text-base text-muted-foreground max-w-3xl mx-auto mb-10 leading-relaxed"
          >
            Conçu pour les gérants de Carrefour Express, City, Contact, supérettes franchisées et épiceries de proximité — 100 % conforme à la{" "}
            <Link to="/convention-idcc-1505" className="text-primary hover:underline font-medium">
              convention collective IDCC 1505
            </Link>
            .
          </motion.p>

          {/* Trial banner */}
          <motion.div
            custom={5}
            initial="hidden"
            animate="visible"
            variants={fadeUpVariants}
            className="inline-flex flex-wrap justify-center items-center gap-2 md:gap-4 px-6 py-3 rounded-2xl glass-pill mb-10"
          >
            <div className="flex items-center gap-2">
              <SuccessIcon className="w-5 h-5 text-success shrink-0" />
              <span className="text-payfit-body text-foreground font-semibold">Essai gratuit 14 jours</span>
            </div>
            <span className="text-border hidden sm:inline">|</span>
            <span className="text-payfit-body text-muted-foreground">Sans carte bancaire</span>
            <span className="text-border hidden sm:inline">|</span>
            <span className="text-payfit-body text-muted-foreground">Sans engagement</span>
          </motion.div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
            {/* Halo pulsé deux fois à l'arrivée pour désigner l'action principale, puis immobile. */}
            <Button
              size="lg"
              asChild
              className="w-full sm:w-auto text-base px-8 h-14 rounded-2xl group animate-pulse-glow"
              style={{ animationIterationCount: 2 }}
            >
              <Link
                to="/auth?mode=signup"
                onClick={() => trackLandingCta("hero_try_free", "Essayer Equiply gratuitement")}
              >
                Essayer Equiply gratuitement
                <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-base ease-out-expo group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="w-full sm:w-auto text-base px-8 h-14 rounded-2xl glass-pill border-border/60">
              <Link
                to="/auth?mode=login"
                onClick={() => trackLandingCta("hero_login", "Se connecter")}
              >
                Se connecter
              </Link>
            </Button>
          </div>
        </div>

        {/* Social Proof Logos */}
        <motion.div
          custom={7}
          initial="hidden"
          animate="visible"
          variants={fadeUpVariants}
          className="max-w-4xl mx-auto mb-20 text-center"
        >
          <p className="text-sm font-semibold text-muted-foreground mb-6 tracking-wide uppercase">Ils simplifient leur quotidien avec nous</p>
          <div className="flex flex-wrap items-center justify-center gap-10 md:gap-20 opacity-60 grayscale hover:grayscale-0 transition-[filter] duration-slow">
             <img src="/logo/express.svg" alt="Carrefour Express" className="h-8 md:h-10 object-contain" />
             <img src="/logo/city.svg" alt="Carrefour City" className="h-8 md:h-10 object-contain" />
             <img src="/logo/contact.svg" alt="Carrefour Contact" className="h-8 md:h-10 object-contain" />
          </div>
        </motion.div>

        {/* Interactive product demo */}
        <div className="relative max-w-[1180px] mx-auto perspective-[2000px]">
          {/* Curved decorative lines */}
          <svg className="absolute -top-20 -left-20 w-[120%] h-[120%] z-0 pointer-events-none hidden md:block" viewBox="0 0 1000 800" preserveAspectRatio="xMidYMid slice">
             <path d="M 500,400 Q 200,400 100,300" fill="none" stroke="var(--primary)" strokeWidth="2" strokeDasharray="4 4" className="opacity-40" />
             <circle cx="100" cy="300" r="4" fill="var(--primary)" className="opacity-80" />
             <path d="M 500,400 Q 800,300 900,200" fill="none" stroke="var(--primary)" strokeWidth="2" strokeDasharray="4 4" className="opacity-40" />
             <circle cx="900" cy="200" r="4" fill="var(--primary)" className="opacity-80" />
          </svg>

          <InteractiveDemo />

          {/* Floating UI Elements */}
          <motion.div
            custom={10}
            initial="hidden"
            animate="visible"
            variants={popInVariants}
            className="hidden lg:block absolute -left-6 xl:-left-20 top-[35%] z-20"
          >
            <div className="bg-card border border-border/80 rounded-2xl p-4 animate-float shadow-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                  <SuccessIcon className="w-5 h-5 text-success" />
                </div>
                <div>
                  <div className="text-payfit-body text-foreground font-semibold">Email envoyé</div>
                  <div className="text-xs text-muted-foreground">4 salariés notifiés</div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            custom={11}
            initial="hidden"
            animate="visible"
            variants={popInVariants}
            className="hidden lg:block absolute -right-4 xl:-right-16 top-[12%] z-20"
          >
            <div className="bg-card border border-border/80 rounded-2xl p-4 animate-float [animation-delay:2s] shadow-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <ShieldIcon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-payfit-body text-foreground font-semibold">100% conforme</div>
                  <div className="text-xs text-muted-foreground">IDCC 1505 vérifiée</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
